import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { PLAN_LIMITS, type PlanType as SharedPlanType } from '../../shared/limits.config';

// ============================================
// PLANS - Система тарифных планов
// ============================================

/**
 * Получить план владельца проекта из Firestore
 * @param ownerId - ID владельца проекта
 * @returns Promise с типом плана
 */
export const getOwnerPlan = async (ownerId: string): Promise<PlanType> => {
  if (!ownerId) return 'free';
  
  try {
    const ownerDoc = await getDoc(doc(db, 'users', ownerId));
    if (ownerDoc.exists() && ownerDoc.data().plan) {
      return ownerDoc.data().plan as PlanType;
    }
    return 'free';
  } catch (error) {
    console.error('[getOwnerPlan] Error fetching owner plan:', error);
    return 'free';
  }
};

/**
 * Доступные тарифные планы
 */
export type PlanType = SharedPlanType;

/**
 * Интерфейс лимитов плана
 */
export interface PlanLimits {
  maxProjects: number;
  maxVersions: number;
  maxScreens: number;
  maxEvents: number;
  maxMembers: number;
  maxDemoProjects: number; // Всегда 3, независимо от плана
}

/**
 * Интерфейс описания плана
 */
export interface Plan {
  id: PlanType;
  name: string;
  description: string;
  limits: PlanLimits;
  isDefault: boolean;
}

/**
 * Словарь планов
 * ВНИМАНИЕ: Лимиты теперь импортируются из shared/limits.config.ts
 * Это единый источник истины для всех лимитов
 */
export const PLANS: Record<PlanType, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    description: 'Базовий план для знайомства з системою',
    limits: PLAN_LIMITS.free,
    isDefault: true,
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    description: 'Розширений план для активної роботи',
    limits: PLAN_LIMITS.pro,
    isDefault: false,
  },
  team: {
    id: 'team',
    name: 'Team',
    description: 'План для командної роботи',
    limits: PLAN_LIMITS.team,
    isDefault: false,
  },
};

/**
 * Получить план по умолчанию (Free)
 */
export const getDefaultPlan = (): Plan => PLANS.free;

/**
 * Получить план по типу
 */
export const getPlan = (planType: PlanType): Plan => PLANS[planType] || PLANS.free;

/**
 * Получить лимиты плана
 */
export const getPlanLimits = (planType: PlanType): PlanLimits => {
  return PLANS[planType]?.limits || PLANS.free.limits;
};

/**
 * Константы лимитов (для совместимости с существующим кодом)
 * Демо-проекты всегда ограничены 3-мя, независимо от плана
 */
export const DEMO_LIMITS = {
  MAX_DEMO_PROJECTS: 3,
} as const;

/**
 * Интерфейс для расширенных лимитов с учетом плана
 */
export interface UserLimits {
  // Текущее использование
  projectCount: number;
  demoProjectCount: number;
  // Максимумы (берутся из плана)
  maxProjects: number;
  maxVersions: number;
  maxScreens: number;
  maxEvents: number;
  maxMembers: number;
  maxDemoProjects: number;
  // Текущий план
  plan: PlanType;
}

/**
 * Функция для создания объекта лимитов с учетом плана
 */
export const createUserLimits = (
  planType: PlanType,
  projectCount: number,
  demoProjectCount: number
): UserLimits => {
  const plan = getPlan(planType);
  return {
    projectCount,
    demoProjectCount,
    maxProjects: plan.limits.maxProjects,
    maxVersions: plan.limits.maxVersions,
    maxScreens: plan.limits.maxScreens,
    maxEvents: plan.limits.maxEvents,
    maxMembers: plan.limits.maxMembers,
    maxDemoProjects: plan.limits.maxDemoProjects,
    plan: planType,
  };
};

/**
 * Проверить, достигнут ли лимит проектов
 */
export const isProjectLimitReached = (limits: UserLimits): boolean => {
  return limits.projectCount >= limits.maxProjects;
};

/**
 * Проверить, достигнут ли лимит демо-проектов
 */
export const isDemoProjectLimitReached = (limits: UserLimits): boolean => {
  return limits.demoProjectCount >= limits.maxDemoProjects;
};

/**
 * Проверить, можно ли добавить участника в проект
 */
export const canAddMember = (limits: UserLimits, currentMembersCount: number): boolean => {
  return currentMembersCount < limits.maxMembers;
};
